import { useEffect } from 'react';
import { BusinessType, Skeleton, withStyles } from 'vopay';
import { useTranslation } from 'react-i18next';
import { Button, DetailsCard, Title } from 'UI';
import StepShell from '@/components/Entities/Accounts/AccountCreation/StepShell/StepShell';
import useDetailsFormConfig, { isSubAccountMode } from './useDetailsFormConfig';
import type { ParentAccountOption } from './parentAccountOptions';
import type { DetailsFormValues } from './types';

import styles from './DetailsStep.module.scss';

const cn = withStyles(styles);

type DetailsStepProps = {
  steps: string[];
  currentStep: number;
  businessTypes: { [businessTypeKey: string]: BusinessType };
  parentAccountOptions: ParentAccountOption[];
  isParentFieldShown: boolean;
  /** The parent list is still loading and nothing is listed yet: the card shows a skeleton in place of the field. */
  isLoadingParentAccounts: boolean;
  lockedParentAccountID?: string;
  nextStep: () => void;
  onValuesChange: (values: DetailsFormValues) => void;
};

export default function DetailsStep({
  steps,
  currentStep,
  businessTypes,
  parentAccountOptions,
  isParentFieldShown,
  isLoadingParentAccounts,
  lockedParentAccountID,
  nextStep,
  onValuesChange,
}: DetailsStepProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'PartnerAccountCreation.Details' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const { ctx, renderSection } = useDetailsFormConfig({
    businessTypes,
    parentAccountOptions,
    isParentFieldShown,
    lockedParentAccountID,
    onSubmit: nextStep,
  });

  useEffect(() => {
    onValuesChange(ctx.values);
  }, [ctx.values]);

  const isSubAccount = isSubAccountMode(ctx.values);

  return (
    <form onSubmit={ctx.handleSubmit} className={cn('form')}>
      <StepShell
        title={t('title', { defaultValue: 'Details' })}
        description={
          isSubAccount
            ? t('subAccountDescription', {
                defaultValue: 'Basic information for the new sub-account. Everything else comes from its parent.',
              })
            : t('description', { defaultValue: 'Basic contact & business information for VoPay account creation.' })
        }
        steps={steps}
        currentStep={currentStep}
        footer={
          <Button colorScheme="primary" type="submit">
            {tGlobal('Buttons.next', { defaultValue: 'Next' })}
          </Button>
        }
      >
        {/* Above Business Information because it decides what the rest of the step asks for (spec 5.2). */}
        {(isParentFieldShown || isLoadingParentAccounts) && (
          <DetailsCard className={cn('sectionCard')} data-testid="parentAccountSection">
            {isParentFieldShown ? (
              renderSection('parentAccount')
            ) : (
              <Skeleton height={92} data-testid="parentAccountLoading" />
            )}
          </DetailsCard>
        )}

        {isSubAccount ? (
          <DetailsCard className={cn('sectionCard')} data-testid="subAccountInfoSection">
            <Title level="4">{t('businessInfo', { defaultValue: 'Business Information' })}</Title>
            <div className={cn('fieldGrid')}>{renderSection('subAccountInfo')}</div>
          </DetailsCard>
        ) : (
          <>
            <DetailsCard className={cn('sectionCard')}>
              <Title level="4">{t('businessInfo', { defaultValue: 'Business Information' })}</Title>
              <div className={cn('fieldGrid')}>{renderSection('businessInfo')}</div>
            </DetailsCard>

            <DetailsCard className={cn('sectionCard')}>
              <Title level="4">{t('businessAddress', { defaultValue: 'Business Address' })}</Title>
              <div className={cn('fieldGrid')}>{renderSection('businessAddress')}</div>
            </DetailsCard>

            <DetailsCard className={cn('sectionCard')}>
              <Title level="4">{t('primaryContact', { defaultValue: 'Primary Contact Information' })}</Title>
              <div className={cn('fieldGrid')}>{renderSection('primaryContact')}</div>
            </DetailsCard>
          </>
        )}
      </StepShell>
    </form>
  );
}
